import {EventDetailsDto, EventDetailsLine, EventDetailsOrder} from "../../../../../models/event/event-details";
import {TimeIntervalBO} from "../time/models/time-interval-bo";
import {SalesWrapper} from "./sales-wrapper";
import {CompositionTypeNl} from "../../../../../models/composition-type/composition-type-nl";
import {TaxUtilities} from "../../../../../utils/tax-utilities";
import {SourceDetailDto} from "../../../../../models/source-detail/source-detail-dto";
import {LinkedDtoRepository} from "../../../common/repositories/linked-dto-repository";
import {SalesDto} from "../../models/sales/sales-dto";
import {ModelBase} from "../../../../../models/model-base";
import {PaymentMethodTranslation} from "../../../../../mappings/payment-method-translation";
import {CompositionDto} from "../../../../../models/composition-dto";
import {CompositionVariantDto} from "../../models/sales/composition-variant-dto";
import {TimeIntervalHelper} from "../time/time-interval-helper";
import {VATRateHelper} from "../../../../../models/common/vat-rate";
import {CompositionVariationCost} from "../../../common/models/costs/composition-variation-cost";
import {BookingCalculation} from "../../../../../_shared/calculation/booking-calculation";
import {compositionVariantKey, compositionVariantLabel} from "../../../../../_shared/composition-grouping";

export class SalesUtils {
  static defaultCompositionTypeId = "default";

  static createBaseOrderElementsWrapperFromEventEdto(eventDto: EventDetailsDto, timeIntervalBO: TimeIntervalBO): SalesWrapper {
    let salesWrapper = new SalesWrapper();
    salesWrapper.setBaseOrderElements(SalesUtils.createBaseOrderElementsFromEventEdto(eventDto, timeIntervalBO))
    return salesWrapper;
  }

  static createBaseOrderElementsFromEventEdto(eventDto: EventDetailsDto, timeIntervalBO: TimeIntervalBO): SalesDto[] {
    let baseOrders: SalesDto[] = [];
    for (let order of eventDto.orders) {
      baseOrders = baseOrders.concat(SalesUtils.orderEdtoToBaseOrderElements(order, timeIntervalBO));
    }

    return baseOrders;
  }

  static orderEdtoToBaseOrderElements(order: EventDetailsOrder, timeIntervalBO: TimeIntervalBO): SalesDto[] {
    let baseOrders: SalesDto[] = [];
    let locationId = order.location.id;
    let orderId = order.id;
    let timeInterval = TimeIntervalHelper.dateToIntervalIndexTIBO(order.created, timeIntervalBO);
    let bookingType = order.bookingType;

    for (let composition of order.compositions) {
      let compositionTypeId = SalesUtils.mapCompositionTypeId(composition.composition.compositionType)
      let pricePerUnit = this.getPricePerUnit(composition, order)
      let quantity = composition.quantity;
      let id = composition.id
      let compositionId = composition.composition.id;
      let compositionCompositeId = composition.compositeId;
      let compositionName = composition.composition.name;
      // The server now ships the decided „Sonstiges" flag rather than the tag rows it was decided from
      // (#760); this used to be `hasSpecialTag(composition.composition.tags)`, which walked the list looking
      // for the same `other` flag — and threw outright when a `?fields=` string forgot `Tags{Other}`.
      let hasSpecialTag = composition.composition.other;

      let baseOrder = new SalesDto(id, locationId, timeInterval, compositionTypeId, orderId, pricePerUnit, quantity, compositionId, compositionCompositeId, compositionName, bookingType, true, hasSpecialTag);
      // #534: set after construction — the positional constructor is already unwieldy. No `?? []` guard any
      // more (#760): `modificationLines` is a required field of the fixed response DTO, so it is an array or
      // the response is malformed. It used to be optional because a `?fields=` projection that did not name
      // it shipped the composition with no such property at all.
      baseOrder.compositionVariantKey = compositionVariantKey(compositionCompositeId, compositionId);
      baseOrder.modificationLines = composition.modificationLines;
      baseOrders.push(baseOrder);
    }
    return baseOrders;
  }

  static mapCompositionTypeId(compositionTypeId: string | undefined): string {

    return compositionTypeId ?? SalesUtils.defaultCompositionTypeId;
  }

  /**
   * The canonical distributed unit price of a booked line (#142): the transmitted single price
   * (`linker.price` = base/override + swap price changes) with the order-level business-case total
   * distributed pro rata — the SAME formula time-performance analytics and employee sales use, so
   * single-event revenue now matches them (this deliberately changed the numbers for orders carrying
   * order-level business cases).
   */
  static getPricePerUnit(linker: EventDetailsLine, order: EventDetailsOrder): number {
    let businessCaseTotal = BookingCalculation.businessCaseTotalSigned(order.businessCaseInformations);
    let orderTotal = BookingCalculation.orderCompositionTotal(
      order.compositions.map(line => ({quantity: line.quantity, singlePrice: line.price})));
    return BookingCalculation.distributedUnitPrice(
      BookingCalculation.totalPrice(linker.quantity, linker.price), orderTotal, businessCaseTotal, linker.quantity);
  }

  static getSourceDetailCost(salesDto: SalesDto): number {
    return salesDto.sourceDetailsCost * salesDto.quantity;
  }


  static getRevenue(salesDto: SalesDto): number {

    return salesDto.pricePerUnit * salesDto.quantity;
  }

  static getRevenueAfterTax(salesDto: SalesDto): number {
    return salesDto.revenueAfterTax;
  }

  static getRevenueExcludingSourceDetails(salesDto: SalesDto): number {
    return (salesDto.pricePerUnit - salesDto.sourceDetailsCost) * salesDto.quantity;
  }

  static getRevenueExcludingTaxesAndSourceDetails(salesDto: SalesDto): number {
    return salesDto.revenueAfterTax - salesDto.sourceDetailsCost * salesDto.quantity;
  }

  /**
   * Sorts the List of BaseOrderElements to save time
   * @param baseOrderElements
   */
  static getMedianPriceOfBaseOderList(baseOrderElements: SalesDto[]): number {
    baseOrderElements.sort((a, b) => a.pricePerUnit - b.pricePerUnit);
    let length = baseOrderElements.length;
    if (length % 2 === 0) {
      return (baseOrderElements[length / 2 - 1].pricePerUnit + baseOrderElements[length / 2].pricePerUnit) / 2;
    }
    return baseOrderElements[Math.floor(length / 2)].pricePerUnit;
  }

  static sourceDetailsToModelList(sourceDetails: CompositionVariationCost[]): SourceDetailDto[] {

    return sourceDetails.map(x => {
      let model = new SourceDetailDto();
      model.id = x.compositeId;
      model.purchasePricePerComposite = x.purchasePrice;
      return model;
    });
  }


  /**
   * Returns a mapping of compositionId to CompositionDto
   * Price is median price of sold compositions
   * @param salesWrapper
   */
  static getCompositionIdMapping(salesWrapper: SalesWrapper): { [id: string]: CompositionDto } {
    return SalesUtils.buildCompositionMapping(
      salesWrapper,
      baseOrderElement => baseOrderElement.compositionId,
      first => {
        let model = new CompositionDto();
        model.name = first.compositionName;
        return model;
      });
  }

  /**
   * The #534 sibling of {@link getCompositionIdMapping}: one entry per composition + swap combination, keyed by
   * `SalesDto.compositionVariantKey`, named with {@link compositionVariantLabel} (the article name plus its
   * `├`/`└` modification lines) and priced with the median of THAT variant — the plain article and its swapped
   * variants genuinely have different prices, so a shared median would misreport both.
   *
   * Registered in `LinkedDtoRepository` under `"compositionVariantKey"`, which is what the table and the top-
   * sales ranking resolve their row names through while "Kombinationen einzeln" is on. In combined mode both
   * mappings still exist — building it is cheap and keeps a mode flip from needing a refetch.
   *
   * Entries are {@link CompositionVariantDto}s, so a consumer can take the article name and the `├`/`└` pairs
   * **separately** instead of splitting the joined label back apart — that is what lets the top-sales ranking
   * render the connector in its own aligned column like the table and the event-details page do.
   */
  static getCompositionVariantKeyMapping(salesWrapper: SalesWrapper): { [id: string]: CompositionDto } {
    return SalesUtils.buildCompositionMapping(
      salesWrapper,
      baseOrderElement => baseOrderElement.compositionVariantKey,
      first => {
        let model = new CompositionVariantDto();
        model.name = compositionVariantLabel(first.compositionName, first.modificationLines);
        model.articleName = first.compositionName;
        model.modificationLines = first.modificationLines;
        return model;
      });
  }

  /**
   * Buckets every sold line under `keyFn` and turns each bucket into the `CompositionDto` `createModel`
   * returns for the bucket's first line, stamping the bucket's key as `id` and its MEDIAN unit price as
   * `price` (the same rule both mappings always used).
   */
  private static buildCompositionMapping(salesWrapper: SalesWrapper,
                                         keyFn: (baseOrderElement: SalesDto) => string,
                                         createModel: (first: SalesDto) => CompositionDto): {
    [id: string]: CompositionDto
  } {
    let compositionDict: { [id: string]: SalesDto[] } = {}
    let compositionMapping: { [id: string]: CompositionDto } = {}

    for (let baseOrderElement of salesWrapper.getBaseOrderElements()) {
      let key = keyFn(baseOrderElement);
      if (compositionDict[key] === undefined) {
        compositionDict[key] = [];
      }
      compositionDict[key].push(baseOrderElement);
    }

    for (let key in compositionDict) {
      let modelBase = createModel(compositionDict[key][0]);
      modelBase.id = key;
      // Note: getMedianPriceOfBaseOderList sorts in place — harmless, the bucket array is built right here.
      modelBase.price = SalesUtils.getMedianPriceOfBaseOderList(compositionDict[key]);
      compositionMapping[key] = modelBase;
    }

    return compositionMapping;
  }


  // Todo in eigene Klasse auslagern
  static paymentMethodToModelDict(): { [id: string]: ModelBase } {
    let paymentMethodModelDict: { [id: string]: ModelBase } = {};
    for (let key in PaymentMethodTranslation.translation) {
      let modelBase = new ModelBase();
      modelBase.id = key;
      modelBase.name = PaymentMethodTranslation.translation[key as unknown as keyof typeof PaymentMethodTranslation.translation] as string;
      paymentMethodModelDict[key] = modelBase;
    }
    return paymentMethodModelDict;
  }

  static getTotalRevenue(salesWrapper: SalesWrapper) {
    return salesWrapper.getBaseOrderElements().reduce((acc, baseOrderElement) => acc + SalesUtils.getRevenue(baseOrderElement), 0);
  }

  static calculateTotalRevenueAfterTax(salesWrapper: SalesWrapper, linkedDtoRepository: LinkedDtoRepository<any>) {
    let compositionTypeMapping = linkedDtoRepository.getMapping("compositionTypeId") as {
      [id: string]: CompositionTypeNl
    };
    for (let baseOrderElement of salesWrapper.getBaseOrderElements()) {
      baseOrderElement.revenueAfterTax = TaxUtilities.getNetFromGross(baseOrderElement.pricePerUnit * baseOrderElement.quantity, VATRateHelper.GetValueOrDefault(compositionTypeMapping[baseOrderElement.compositionTypeId]?.vatRate));
    }
  }

  static setSourceDeailsCost(salesWrapper: SalesWrapper, sourceDetails: LinkedDtoRepository<any>) {
    let sourceDetailsModelDict = sourceDetails.getMapping("sourceDetailsCost") as { [id: string]: SourceDetailDto };
    for (let baseOrderElement of salesWrapper.getBaseOrderElements()) {
      baseOrderElement.sourceDetailsCost = sourceDetailsModelDict[baseOrderElement.compositionCompositeId].purchasePricePerComposite ?? 0;
    }
  }

  static createDuplicatedZeroRevenueSet(salesWrapper: SalesWrapper): {
    employeeSalesZeroRevenueSet: { [id: string]: SalesDto },
    employeeSalesZeroRevenueSetOriginal: { [id: string]: SalesDto }
  } {
    let swapOrderElementsSet: {
      employeeSalesZeroRevenueSet: { [id: string]: SalesDto },
      employeeSalesZeroRevenueSetOriginal: { [id: string]: SalesDto }
    } = {employeeSalesZeroRevenueSet: {}, employeeSalesZeroRevenueSetOriginal: {}};
    for (let baseOrderElement of salesWrapper.getBaseOrderElements()) {
      if (baseOrderElement.bookingType !== 2) continue;
      let duplicatedBaseOrderElement = SalesDto.copy(baseOrderElement);
      duplicatedBaseOrderElement.revenueAfterTax = 0;
      duplicatedBaseOrderElement.pricePerUnit = 0;
      swapOrderElementsSet.employeeSalesZeroRevenueSet[duplicatedBaseOrderElement.id] = duplicatedBaseOrderElement;
      swapOrderElementsSet.employeeSalesZeroRevenueSetOriginal[baseOrderElement.id] = baseOrderElement;
    }
    return swapOrderElementsSet;
  }

  static swapZeroRevenueSet(salesWrapper: SalesWrapper, salesWrapperCache: SalesWrapper, swapOrderElementsSet: {
    employeeSalesZeroRevenueSet: { [id: string]: SalesDto },
    employeeSalesZeroRevenueSetOriginal: { [id: string]: SalesDto }
  }, direction: boolean) {
    let length = salesWrapper.getBaseOrderElements().length;
    for (let i = 0; i < length; i++) {
      if (salesWrapper.getBaseOrderElements()[i].bookingType !== 2) continue;
      if (direction) {
        salesWrapper.swapElements(salesWrapper.getBaseOrderElements()[i], swapOrderElementsSet.employeeSalesZeroRevenueSet[salesWrapper.getBaseOrderElements()[i].id], i)
      } else {
        salesWrapper.swapElements(salesWrapper.getBaseOrderElements()[i], swapOrderElementsSet.employeeSalesZeroRevenueSetOriginal[salesWrapper.getBaseOrderElements()[i].id], i)
      }
    }
    length = salesWrapperCache.getBaseOrderElements().length;
    for (let i = 0; i < length; i++) {
      if (salesWrapperCache.getBaseOrderElements()[i].bookingType !== 2) continue;
      if (direction) {
        salesWrapperCache.swapElements(salesWrapperCache.getBaseOrderElements()[i], swapOrderElementsSet.employeeSalesZeroRevenueSet[salesWrapperCache.getBaseOrderElements()[i].id], i)
      } else {
        salesWrapperCache.swapElements(salesWrapperCache.getBaseOrderElements()[i], swapOrderElementsSet.employeeSalesZeroRevenueSetOriginal[salesWrapperCache.getBaseOrderElements()[i].id], i)
      }
    }
  }


}
